import { RadioGroup } from '@headlessui/react';
import type { EnhancedStoreCustomer } from '@marxbiotech/shared-types/medusa';
import { Button, Checkbox, Input, toast } from '@medusajs/ui';
import { useState } from 'react';

import { useUpdateCustomer } from '@lib/query/mutations/customer';
import { Divider } from '@modules/common/components/divider';
import { FormField, FormLabel } from '@modules/common/components/form-field';
import Radio from '@modules/common/components/radio';

interface OverviewProps {
  customer: EnhancedStoreCustomer;
}

const inputClass =
  'mt-1 h-12 rounded-md border border-white-40 bg-white-10 px-2 py-2 shadow-none';

const Overview: React.FC<OverviewProps> = ({ customer }) => {
  const [dirty, setDirty] = useState(false);
  const [phone, setPhone] = useState(customer.phone ?? '');
  const [contactName, setContactName] = useState(
    (customer.metadata?.contact_name as string) ?? ''
  );
  const [contactPhone, setContactPhone] = useState(
    (customer.metadata?.contact_phone as string) ?? ''
  );
  const [sameAsMember, setSameAsMember] = useState(true);
  const shouldIssueInvoiceWithTitle =
    customer.metadata?.should_issue_invoice_with_title as boolean | undefined;

  const updateCustomer = useUpdateCustomer({
    onSuccess: () => {
      toast.success('會員資料已更新', { duration: 4000 });
      setDirty(false);
    },
    onError: () => {
      toast.error('更新失敗，請稍後再試', { duration: 4000 });
    },
  });

  const handleCheckboxChange = () => {
    setDirty(true);
    const newValue = !sameAsMember;
    setSameAsMember(newValue);
    if (newValue) {
      setContactName(customer.last_name ?? '');
      setContactPhone(phone);
    }
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    updateCustomer.mutate({
      phone,
      metadata: {
        contact_name: contactName,
        contact_phone: contactPhone,
      },
    });
  };

  return (
    <div className="w-full" data-testid="profile-page-wrapper">
      <div className="mb-8 flex items-center">
        <span className="text-black-30 mr-1 text-3xl font-bold">會員資料</span>
        <span className="text-grey-60 text-sm">*為必填欄位</span>
      </div>
      <form onSubmit={handleSubmit}>
        <input type="hidden" name="id" value={customer.id} />
        <div className="flex w-full flex-col gap-y-6">
          <FormField>
            <FormLabel>公司名稱*</FormLabel>
            <Input
              className={inputClass}
              placeholder="請輸入公司名稱"
              name="company_name"
              value={customer.company_name ?? ''}
              required
              autoComplete="company-name"
              data-testid="company-name-input"
              disabled
            />
          </FormField>
          <FormField>
            <FormLabel>公司地址*</FormLabel>
            <Input
              className={inputClass}
              placeholder="請輸入公司地址"
              name="metadata[company_address]"
              value={
                typeof customer.metadata?.company_address === 'string'
                  ? customer.metadata.company_address
                  : undefined
              }
              required
              autoComplete="company-address"
              data-testid="company-address-input"
              disabled
            />
          </FormField>
          <div className="grid grid-cols-2 gap-6">
            <FormField className="col-span-1">
              <FormLabel>會員姓名*</FormLabel>
              <Input
                id="last_name"
                className={inputClass}
                placeholder="請輸入會員姓名"
                name="last_name"
                value={customer.last_name ?? ''}
                required
                autoComplete="last-name"
                data-testid="last-name-input"
                disabled
              />
            </FormField>
            <FormField className="col-span-1">
              <FormLabel>電話號碼*</FormLabel>
              <Input
                className={inputClass}
                placeholder="請輸入電話號碼"
                name="phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                onFocus={() => setDirty(true)}
                required
                autoComplete="phone"
                data-testid="phone-input"
              />
            </FormField>
          </div>
          <FormField>
            <FormLabel>電子信箱*</FormLabel>
            <Input
              className={inputClass}
              placeholder="請輸入電子信箱"
              name="email"
              value={customer.email ?? ''}
              required
              autoComplete="email"
              data-testid="company-email-input"
              disabled
            />
          </FormField>
          <div className="grid grid-cols-2 gap-6">
            <FormField className="col-span-1">
              <FormLabel>統一編號*</FormLabel>
              <Input
                className={inputClass}
                placeholder="請輸入統一編號"
                name="metadata[gui_number]"
                value={
                  typeof customer.metadata?.gui_number === 'string'
                    ? customer.metadata.gui_number
                    : undefined
                }
                required
                autoComplete="gui-number"
                data-testid="gui-number-input"
                disabled
              />
            </FormField>
            <FormField className="col-span-1">
              <FormLabel>醫事機構代碼</FormLabel>
              <Input
                className={inputClass}
                placeholder="請輸入醫事機構代碼"
                name="metadata[hosp_id]"
                value={
                  typeof customer.metadata?.hosp_id === 'string'
                    ? customer.metadata.hosp_id
                    : undefined
                }
                autoComplete="hosp-id"
                data-testid="hosp-id-input"
                disabled
              />
            </FormField>
          </div>
          <div className="flex gap-6">
            <FormLabel>發票是否開立抬頭及統編</FormLabel>
            <RadioGroup
              className="flex gap-4"
              value={shouldIssueInvoiceWithTitle}
              name="should_issue_invoice_with_title"
              disabled
            >
              <div className="flex items-center">
                <Radio
                  disabled
                  checked={shouldIssueInvoiceWithTitle === true}
                />
                <div className="ml-2">是</div>
              </div>
              <div className="flex items-center">
                <Radio disabled checked={!shouldIssueInvoiceWithTitle} />
                <div className="ml-2">否</div>
              </div>
            </RadioGroup>
          </div>
          <Divider />

          <div className="mt-[-0.5rem] flex items-center space-x-2">
            <Checkbox checked={sameAsMember} onClick={handleCheckboxChange} />
            <FormLabel>同會員資訊</FormLabel>
          </div>
          <div className="grid grid-cols-2 gap-6">
            <FormField>
              <FormLabel>財務人員姓名*</FormLabel>
              <input
                className={inputClass}
                placeholder="請輸入財務人員姓名"
                name="metadata[contact_name]"
                required
                value={contactName}
                onFocus={() => setDirty(true)}
                onChange={(e) => setContactName(e.target.value)}
                autoComplete="contact-name"
                data-testid="contact-name-input"
              />
            </FormField>
            <FormField>
              <FormLabel>財務人員電話號碼*</FormLabel>
              <input
                className={inputClass}
                placeholder="請輸入財務人員電話號碼"
                name="metadata[contact_phone]"
                required
                value={contactPhone}
                onFocus={() => setDirty(true)}
                onChange={(e) => setContactPhone(e.target.value)}
                autoComplete="contact-phone"
                data-testid="contact-phone-input"
              />
            </FormField>
          </div>
        </div>
        {updateCustomer.isError && (
          <div className="text-xs font-normal leading-5 text-red-60 py-2">
            更新失敗，請稍後再試
          </div>
        )}

        <div className="my-16 flex items-center justify-center">
          <Button
            className="bg-primary-50 text-white-10 disabled:bg-white-40 disabled:text-white-10 rounded-sm px-6 py-3 text-base font-bold shadow-none disabled:shadow-none"
            data-testid="save-button"
            type="submit"
            isLoading={updateCustomer.isPending}
            disabled={!dirty}
          >
            確認儲存
          </Button>
        </div>
      </form>
    </div>
  );
};

export default Overview;
